import React, { useState } from 'react';
import { createInvitation } from '../services/api';

/**
 * InviteForm - Form for admins to invite new members
 *
 * @param {Function} onInviteSent - Callback when invitation is created successfully
 */
export default function InviteForm({ onInviteSent }) {
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('member');
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState(null);

  const roles = [
    { value: 'member', label: 'Member', description: 'Can book slots and give speeches' },
    { value: 'evaluator', label: 'Evaluator', description: 'Can provide feedback on speeches' },
    { value: 'admin', label: 'Admin', description: 'Full access to manage the club' },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;

    setSending(true);
    setMessage(null);

    try {
      const res = await createInvitation(email.trim(), role);

      setMessage({
        type: 'success',
        text: `Invitation sent to ${email}`,
        inviteUrl: res.inviteUrl,
      });
      setEmail('');
      setRole('member');

      if (onInviteSent) onInviteSent(res.invitation);
    } catch (err) {
      let errorText = err.message || 'Failed to send invitation';

      if (err.code === 'ALREADY_MEMBER') {
        errorText = 'This email is already a club member.';
      } else if (err.code === 'INVITE_EXISTS') {
        errorText = 'A pending invitation already exists for this email.';
      } else if (err.code === 'INVALID_EMAIL') {
        errorText = 'Please enter a valid email address.';
      }

      setMessage({ type: 'error', text: errorText });
    } finally {
      setSending(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
        {/* Email Input */}
        <div className="flex-1">
          <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
            Email Address
          </label>
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="colleague@example.com"
            className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2"
            style={{ borderColor: 'var(--border-subtle)' }}
            required
            disabled={sending}
          />
        </div>

        {/* Role Select */}
        <div className="w-full sm:w-36">
          <label className="block text-sm font-medium mb-1" style={{ color: 'var(--text-secondary)' }}>
            Role
          </label>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value)}
            className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2"
            style={{ borderColor: 'var(--border-subtle)' }}
            disabled={sending}
          >
            {roles.map((r) => (
              <option key={r.value} value={r.value}>
                {r.label}
              </option>
            ))}
          </select>
        </div>

        {/* Submit Button */}
        <button
          type="submit"
          disabled={sending || !email.trim()}
          className="px-4 py-2 rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed text-sm font-medium whitespace-nowrap"
          style={{ background: 'var(--accent)', color: 'var(--on-primary, #fff)' }}
        >
          {sending ? 'Sending…' : 'Send Invite'}
        </button>
      </div>

      {/* Role Description */}
      <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
        {roles.find((r) => r.value === role)?.description}
      </p>

      {/* Success/Error Message */}
      {message && (
        <div
          className="px-4 py-3 rounded-lg text-sm"
          style={
            message.type === 'success'
              ? {
                  background: 'var(--color-success-dim)',
                  color: 'var(--color-success)',
                  border: '1px solid var(--color-success-border)',
                }
              : {
                  background: 'var(--color-danger-dim)',
                  color: 'var(--color-danger)',
                  border: '1px solid var(--color-danger-border)',
                }
          }
        >
          <p className="break-words">{message.text}</p>
          {message.inviteUrl && (
            <div className="mt-2">
              <p className="text-xs mb-1" style={{ color: 'var(--color-success)', opacity: 0.85 }}>Invite link (copy if email delivery fails):</p>
              <input
                type="text"
                readOnly
                value={message.inviteUrl}
                className="w-full px-2 py-1 text-xs border rounded select-all"
                style={{ background: 'var(--bg-surface)', borderColor: 'var(--color-success-border)' }}
                onClick={(e) => e.target.select()}
              />
            </div>
          )}
        </div>
      )}
    </form>
  );
}
